import React, { useEffect, useState, useCallback, useMemo } from 'react'; // Added useMemo
import { collection, getDocs, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useLocation } from 'react-router-dom'; // Import useLocation
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css'; // Import calendar CSS
import './JadwalPenggunaan.css'; // Your custom CSS
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Grid, Paper } from '@mui/material';

// Setup the localizer by providing the moment Object
const localizer = momentLocalizer(moment);

// Helper to convert Firestore Timestamp to JS Date
const timestampToDate = (timestamp) => {
  return timestamp instanceof Timestamp ? timestamp.toDate() : null;
};

const JadwalPenggunaan = () => {
  const [instruments, setInstruments] = useState([]);
  const [selectedInstrument, setSelectedInstrument] = useState(null); // Store the selected instrument object
  const [scheduleEvents, setScheduleEvents] = useState([]);
  const [loadingInstruments, setLoadingInstruments] = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState(''); // State for search term
  const location = useLocation(); // Get location object
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  // Define fetchScheduleForInstrument first using useCallback
  const fetchScheduleForInstrument = useCallback(async (instrument) => {
    if (!instrument) return;
    setLoadingSchedule(true);
    setError('');
    setSelectedInstrument(instrument); // Set the selected instrument
    setScheduleEvents([]); // Clear previous schedule
    try {
      const requestsRef = collection(db, 'permintaan_alat');
      // Query for approved requests for the specific instrument name
      const q = query(
        requestsRef,
        where('status', '==', 'approved'),
        where('namaAlat', '==', instrument.nama), // Filter by selected instrument name
        orderBy('tanggalPinjam', 'asc') // Order by borrow date
      );

      const querySnapshot = await getDocs(q);
      const events = querySnapshot.docs.map(docSnap => {
        const data = docSnap.data();
        const docId = docSnap.id; // Get doc ID for logging
        try { // Add try-catch around each event creation
          const start = timestampToDate(data.tanggalPinjam);
          const end = timestampToDate(data.tanggalKembali);

          // Log the raw data for debugging (optional, uncomment if needed)
          // console.log(`Processing booking ${docId} for ${instrument.nama}:`, data);

          // Ensure start and end dates are valid Date objects
          if (!start || !end) {
            console.warn(`Invalid or missing date found for request ${docId} (${instrument.nama}). Start: ${data.tanggalPinjam}, End: ${data.tanggalKembali}`);
            return null; // Skip this event if dates are invalid
          }

          // Add a small buffer to the end time if start and end are the same day for visibility
          // Clone end date before modifying to avoid side effects
          let adjustedEnd = new Date(end.getTime());
          if (moment(start).isSame(adjustedEnd, 'day')) {
              adjustedEnd.setHours(adjustedEnd.getHours() + 1); // Example: make it last at least an hour on the calendar
          }

          return {
            id: docId,
            title: `Dipinjam oleh: ${data.namaUser || 'N/A'} (${data.jumlah || 1})`, // Show user name and quantity
            start: start,
            end: adjustedEnd, // Use the potentially adjusted end date
            allDay: false, // Assuming bookings are not necessarily all-day
            resource: data, // Optional: include original data
          };
        } catch (mapError) {
          console.error(`Error processing booking document ${docId} for ${instrument.nama}:`, mapError, "Data:", data);
          // Set a general error, but allow other events to load
          setError(`Terjadi kesalahan saat memproses sebagian data jadwal untuk ${instrument.nama}. Beberapa jadwal mungkin tidak tampil.`);
          return null; // Skip faulty record
        }
      }).filter(event => event !== null); // Filter out null events from invalid dates or processing errors

      setScheduleEvents(events);
    } catch (err) {
      console.error(`Error fetching schedule for ${instrument.nama}:`, err);
      setError(`Gagal memuat jadwal untuk ${instrument.nama}.`);
    } finally {
      setLoadingSchedule(false);
    }
  }, []); // Dependency array empty as it doesn't depend on component state/props directly

  // Handle event selection
  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setOpenDialog(true);
  };

  // Close the detail dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Define fetchAlatList using useCallback, depending on fetchScheduleForInstrument
  const fetchAlatList = useCallback(async () => {
    setLoadingInstruments(true);
    setError('');
    try {
      // Only fetch instruments
      const instrumenRef = collection(db, 'instrumen');
      const instrumenSnapshot = await getDocs(query(instrumenRef, orderBy('nama')));
      const instrumenData = instrumenSnapshot.docs.map(doc => ({ id: doc.id, type: 'instrumen', ...doc.data() }));
      setInstruments(instrumenData);

      // --- Auto-select based on location state ---
      if (location.state?.alatId && instrumenData.length > 0) {
        const preselectedAlat = instrumenData.find(item => item.id === location.state.alatId);
        if (preselectedAlat) {
          // Use a timeout to ensure state updates related to list loading are finished
          // before triggering the schedule fetch.
          setTimeout(() => fetchScheduleForInstrument(preselectedAlat), 0);
        } else {
          console.warn(`Preselected alat with ID ${location.state.alatId} not found in the fetched list.`);
        }
      }
      // --- End auto-select ---

    } catch (err) {
      console.error("Error fetching instruments:", err);
      setError('Gagal memuat daftar instrumen.');
    } finally {
      setLoadingInstruments(false);
    }
  }, [location.state, fetchScheduleForInstrument]); // Dependencies: location.state and the function it calls

  // Filtered list based on search term
  const filteredInstruments = useMemo(() => {
    if (!searchTerm) {
      return instruments;
    }
    return instruments.filter(item =>
      item.nama.toLowerCase().includes(searchTerm.toLowerCase())
      // Add other fields to search if needed, e.g., item.kode
    );
  }, [instruments, searchTerm]);

  // useEffect hook to call fetchAlatList
  useEffect(() => {
    fetchAlatList();
  }, [fetchAlatList]); // Depend on the memoized fetchAlatList

  return (
    <div className="jadwal-penggunaan-container">
      <h2>Jadwal Penggunaan Alat</h2>

      {error && <div className="error-message">{error}</div>}

      {/* Alat List (Instruments + Glassware) */}
      <div className="instrument-list-container">
        <h3>Pilih Alat:</h3>

        {/* Search Input */}
        <div className="search-container" style={{ marginBottom: '15px', maxWidth: '400px' }}>
          <input
            type="text"
            placeholder="Cari alat berdasarkan nama..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input" // Reuse class or create a new one
            style={{ width: '100%', padding: '8px 10px', borderRadius: '4px', border: '1px solid #ccc' }}
          />
        </div>

        {loadingInstruments ? (
          <p>Memuat daftar alat...</p>
        ) : (
          <ul className="instrument-list">
            {/* Use filtered list */}
            {filteredInstruments.map((instr) => (
              <li key={instr.id}>
                <button
                  onClick={() => fetchScheduleForInstrument(instr)}
                  className={selectedInstrument?.id === instr.id ? 'selected' : ''}
                  disabled={loadingSchedule} // Disable button while schedule is loading
                >
                  {instr.nama}
                </button>
              </li>
            ))}
            {/* Update empty state message */}
            {filteredInstruments.length === 0 && !loadingInstruments && (
               <li>{searchTerm ? 'Tidak ada alat yang cocok dengan pencarian.' : 'Tidak ada alat yang tersedia.'}</li>
            )}
          </ul>
        )}
      </div>

      {/* Calendar View */}
      {selectedInstrument && (
        <div className="calendar-container">
          <h3>Jadwal untuk: {selectedInstrument.nama}</h3>
          {loadingSchedule ? (
            <div className="loading-message">Memuat jadwal...</div>
          ) : (
            <Calendar
              localizer={localizer}
              events={scheduleEvents}
              startAccessor="start"
              endAccessor="end"
              style={{ height: 500 }} // Adjust height as needed
              views={['month', 'week', 'day']} // Allow different views
              step={60} // Time slot interval in minutes
              showMultiDayTimes // Show times for multi-day events
              onSelectEvent={handleSelectEvent} // Add event selection handler
              messages={{
                today: 'Hari Ini',
                previous: 'Sebelumnya',
                next: 'Berikutnya',
                month: 'Bulan',
                week: 'Minggu',
                day: 'Hari',
                agenda: 'Agenda',
                date: 'Tanggal',
                time: 'Waktu',
                event: 'Acara', // Or 'Peminjaman'
                noEventsInRange: 'Tidak ada jadwal penggunaan dalam rentang ini.',
              }}
            />
          )}
           {scheduleEvents.length === 0 && !loadingSchedule && (
             <p style={{ textAlign: 'center', marginTop: '20px' }}>
               Tidak ada jadwal penggunaan yang disetujui untuk instrumen ini.
             </p>
           )}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Detail Peminjaman Alat
        </DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="h6" gutterBottom>
                    Informasi Alat
                  </Typography>
                  <Typography><strong>Nama Alat:</strong> {selectedInstrument?.nama || 'N/A'}</Typography>
                  {selectedInstrument?.kode && (
                    <Typography><strong>Kode Alat:</strong> {selectedInstrument.kode}</Typography>
                  )}
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="h6" gutterBottom>
                    Informasi Peminjaman
                  </Typography>
                  <Typography><strong>Peminjam:</strong> {selectedEvent.resource?.namaUser || 'N/A'}</Typography>
                  <Typography><strong>NIM/NIP:</strong> {selectedEvent.resource?.nimNip || 'N/A'}</Typography>
                  <Typography><strong>Jumlah:</strong> {selectedEvent.resource?.jumlah || '1'}</Typography>
                  <Typography><strong>Tanggal Pinjam:</strong> {moment(selectedEvent.start).format('DD MMMM YYYY, HH:mm')}</Typography>
                  <Typography><strong>Tanggal Kembali:</strong> {moment(selectedEvent.end).format('DD MMMM YYYY, HH:mm')}</Typography>
                  {selectedEvent.resource?.tujuanPenggunaan && (
                    <Typography><strong>Tujuan Penggunaan:</strong> {selectedEvent.resource.tujuanPenggunaan}</Typography>
                  )}
                  {selectedEvent.resource?.catatanPeminjam && (
                    <Typography><strong>Catatan Peminjam:</strong> {selectedEvent.resource.catatanPeminjam}</Typography>
                  )}
                  {selectedEvent.resource?.catatanAdmin && (
                    <Typography><strong>Catatan Admin:</strong> {selectedEvent.resource.catatanAdmin}</Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Tutup
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default JadwalPenggunaan;
